import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, BehaviorSubject } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CoinTransaction } from '../../../../../models/CoinTransaction';
import { SalesUser } from '../../../../../models/SalesUser';
import { selectCoinTransactions } from '../../../../../state/coin-transaction/coin-transaction.selector';
import { getCoinTransactions } from '../../../../../state/coin-transaction/coin-transaction.actions';
import { selectAllSalesUsers } from '../../../../../state/sales-user/sales-user.selector';
import { loadSalesUsers } from '../../../../../state/sales-user/sales-user.actions';
import { AsyncPipe, CommonModule, DatePipe } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { PageHeaderComponent } from '../../../../page-header/page-header.component';

@Component({
  selector: 'app-sales-users-transaction-history',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, MatFormFieldModule, MatInputModule, DatePipe, AsyncPipe, MatTableModule, CommonModule, PageHeaderComponent],
  templateUrl: './sales-users-transaction-history.component.html',
  styleUrl: './sales-users-transaction-history.component.css'
})
export class SalesUsersTransactionHistoryComponent implements OnInit {
  filter$ = new BehaviorSubject<string>('');
  filteredTransactions$: Observable<CoinTransaction[]>;
  salesUsersWithSum$: Observable<Array<{ user: SalesUser, sum: number }>>;

  constructor(private store: Store) {
    const allTransactions$ = this.store.select(selectCoinTransactions).pipe(map(cts => cts ?? []));
    const allSalesUsers$ = this.store.select(selectAllSalesUsers).pipe(map(users => users ?? []));

    this.filteredTransactions$ = combineLatest([
      allTransactions$,
      this.filter$.pipe(startWith(''))
    ]).pipe(
      map(([transactions, filter]) => {
        if (!filter) return transactions;
        const lower = filter.toLowerCase();
        return transactions.filter(tx => {
          const grantedBy = tx.grantedBy ? `${tx.grantedBy.firstName ?? ''} ${tx.grantedBy.lastName ?? ''}`.toLowerCase() : '';
          const customer = tx.customer?.userDetails ? `${tx.customer.userDetails.firstName ?? ''} ${tx.customer.userDetails.lastName ?? ''}`.toLowerCase() : '';
          return grantedBy.includes(lower) || customer.includes(lower);
        });
      })
    );

    this.salesUsersWithSum$ = combineLatest([allSalesUsers$, allTransactions$]).pipe(
      map(([users, transactions]) => users.map(user => {
        const userId = user.user?.id;
        const sum = transactions
          .filter(tx => tx.grantedBy && tx.grantedBy.id && userId && tx.grantedBy.id === userId)
          .reduce((acc, tx) => acc + (tx.amount ?? 0), 0);
        return { user, sum };
      }))
    );
  }

  ngOnInit(): void {
    this.store.dispatch(getCoinTransactions());
    this.store.dispatch(loadSalesUsers());
  }

  setFilter(value: string) {
    this.filter$.next(value);
  }

  setFilterToUser(user: SalesUser) {
    const name = `${user.user?.firstName ?? ''} ${user.user?.lastName ?? ''}`.trim();
    this.filter$.next(name);
  }

  goBack() {
    window.history.back();
  }
}
